"use client";

import { Button, Icon } from "@pamperedchef/design-system";
import * as styles from "./AddToCartButton.module.scss";

export type AddToCartButtonState =
	| { kind: "idle" }
	| { kind: "loading" }
	| { kind: "success" }
	| { kind: "error"; message: string }
	| { kind: "out-of-stock" }
	| { kind: "discontinued" }
	| { kind: "temporarily-unavailable" }
	| { kind: "pre-launch"; launchDate?: string }
	| { kind: "host-exclusive" }
	| { kind: "consultant-only-gated" };

export interface AddToCartButtonViewProps {
	state: AddToCartButtonState;
	onClick: () => void;
	fullWidth?: boolean;
	className?: string;
}

/**
 * Pure UI for the Add-to-Cart affordance. No hooks, no domain coupling.
 * Wired sibling `AddToCartButton` derives `state` from `CatalogProduct`
 * flags and the `useAddToCart` hook output.
 */
export function AddToCartButtonView({
	state,
	onClick,
	fullWidth = false,
	className
}: AddToCartButtonViewProps) {
	const computed = computeView(state);

	return (
		<div
			className={[
				styles["atc"],
				fullWidth ? styles["atc--full"] : "",
				className ?? ""
			]
				.filter(Boolean)
				.join(" ")}
			data-component="AddToCartButtonView"
			data-state={state.kind}
		>
			<Button
				kind={computed.kind}
				size="lg"
				isDisabled={computed.disabled}
				onPress={computed.disabled ? undefined : onClick}
				aria-busy={state.kind === "loading"}
				className={styles["atc__btn"]}
			>
				{state.kind === "success" ? (
					<span className={styles["atc__success"]}>
						<Icon name="check" size={16} />
						{computed.label}
					</span>
				) : state.kind === "loading" ? (
					<span className={styles["atc__loading"]} aria-hidden>
						{computed.label}
					</span>
				) : (
					computed.label
				)}
			</Button>

			{state.kind === "error" ? (
				<div
					role="alert"
					aria-live="polite"
					className={styles["atc__error"]}
				>
					<Icon name="error" />
					<p>{state.message}</p>
				</div>
			) : null}

			{state.kind === "pre-launch" && state.launchDate ? (
				<p className={styles["atc__hint"]}>
					Available {state.launchDate}
				</p>
			) : null}
		</div>
	);
}

interface ComputedView {
	label: string;
	kind: "primary" | "secondary" | "tertiary";
	disabled: boolean;
}

function computeView(state: AddToCartButtonState): ComputedView {
	switch (state.kind) {
		case "idle":
			return { label: "Add to Cart", kind: "primary", disabled: false };
		case "loading":
			return { label: "Adding…", kind: "primary", disabled: true };
		case "success":
			return { label: "Added to Cart", kind: "primary", disabled: true };
		case "error":
			// Button stays clickable for retry.
			return { label: "Add to Cart", kind: "primary", disabled: false };
		// Non-purchasable states keep the "Add to Cart" label — the DS
		// primary disabled style is the greyed-out button; the status tag
		// (ProductSummary) communicates WHY it's disabled.
		case "out-of-stock":
		case "discontinued":
		case "temporarily-unavailable":
		case "pre-launch":
			return { label: "Add to Cart", kind: "primary", disabled: true };
		case "host-exclusive":
			return { label: "Host a Party", kind: "primary", disabled: false };
		case "consultant-only-gated":
			return {
				label: "Sign in as Consultant",
				kind: "secondary",
				disabled: false
			};
	}
}
